import { Fragment, ReactElement, useEffect, useRef, useState } from "react";

import { useIntl } from "react-intl";
import { Typography } from "@mui/material";
import { BreadcrumbsItem } from "react-breadcrumbs-dynamic";
import { localizeRouteKey } from "@/utils/common.ts";
import { AppRoute } from "@/routes";
import { Redirect, Route, Router, useHistory } from "react-router-dom";
import { useLuvat } from "@/stores/luvat";
import { LocalizedSwitch } from "@/modules/i18n";
import Jarjestajaluettelo from "@/scenes/KaikkiJarjestajat/Jarjestajaluettelo.tsx";
import Loading from "@/scenes/Loading.tsx";
import error from "@/i18n/definitions/error";
import kaikkiJarjestajat from "@/i18n/definitions/kaikkiJarjestajat.ts";
import { SearchState, SortState } from "@/components/02-organisms/CollapsibleTable/types.ts";
import { PagedLuvatByJarjestajatResponse } from "@/stores/luvat";

/** Palauttaa sessionStoragesta aiemmin tallennetun taulukon tilan, jos sellainen on. */
function restoreTableState() {
  try {
    const saved = sessionStorage.getItem("kaikkiJarjestajat.tableState");
    if (saved) {
      sessionStorage.removeItem("kaikkiJarjestajat.tableState");
      return JSON.parse(saved);
    }
  } catch {
    // Virheellinen JSON – sivuutetaan
  }
  return null;
}

const KaikkiJarjestajat = (): ReactElement => {
  const history = useHistory();
  const intl = useIntl();
  const { formatMessage, locale } = intl;

  const [jarjestajat, luvatActions] = useLuvat();
  const url = localizeRouteKey(locale, AppRoute.KaikkiJarjestajat, intl);

  // Säilytetään viimeisin onnistuneesti ladattu data, jotta Jarjestajaluettelo
  // pysyy mountattuna uusien hakujen latauksen aikana (store tyhjentää data:n isLoading-tilaan siirtymisen yhteydessä).
  const lastKnownData = useRef<PagedLuvatByJarjestajatResponse | null>(null);
  if (jarjestajat.paged?.data?.content) {
    lastKnownData.current = jarjestajat.paged.data;
  }
  const displayData = jarjestajat.paged?.data ?? lastKnownData.current;

  // Palautetaan aiemmin tallennettu taulukon tila, jos palataan lupasivulta
  const savedState = restoreTableState();

  const [pageNo, setPageNo] = useState<number>(savedState?.pageNo ?? 1);
  const [pageSize, setPageSize] = useState<number>(savedState?.pageSize ?? 20);
  const [selectedKoulutustyyppi, setSelectedKoulutustyyppi] = useState<string | null>(savedState?.selectedKoulutustyyppi ?? null);
  const [sortedBy, setSortedBy] = useState<SortState | null>(savedState?.sortedBy ?? null);
  const [searchBy, setSearchBy] = useState<SearchState>(savedState?.searchBy ?? {});
  const [openRows, setOpenRows] = useState<Set<string>>(
    new Set<string>(savedState?.openRows ?? [])
  );

  // Hae koulutuksen järjestäjät ja niiden luvat
  useEffect(() => {
    const lang = locale.split(/[-_]/)[0].toLowerCase() === "sv" ? "sv" : "fi";

    const searchKeyMap: Record<string, string> = {
      jarjestaja: `jarjestaja.nimi.${lang}`,
      maakunta: `jarjestaja.maakuntaKoodi.metadata.nimi.${lang}`
    };

    const additionalParams: Array<{ key: string; value: string }> = [];

    if (selectedKoulutustyyppi !== null && selectedKoulutustyyppi !== "__none__") {
      // __ammatillinen__-sentinel lähetetään backendille tyhjänä merkkijonona
      const koulutustyyppiParam =
        selectedKoulutustyyppi === "__ammatillinen__" ? "" : selectedKoulutustyyppi;
      additionalParams.push({ key: "koulutustyyppi", value: koulutustyyppiParam });
    }

    if (sortedBy) {
      additionalParams.push({ key: "sortBy", value: sortedBy.sortBy });
      additionalParams.push({ key: "orderBy", value: sortedBy.orderBy });
    }

    Object.entries(searchBy).forEach(([columnId, value]) => {
      if (value) {
        const searchKey = searchKeyMap[columnId];
        if (searchKey) {
          additionalParams.push({ key: "search", value: `${searchKey}~${value}` });
        }
      }
    });

    const abortController = luvatActions.loadWithPaging(pageNo, pageSize, additionalParams);
    return () => abortController?.abort();
  }, [luvatActions, pageNo, pageSize, selectedKoulutustyyppi, sortedBy, searchBy, locale]);

  const pageTitle = formatMessage(kaikkiJarjestajat.paasivunOtsikko);

  return (
    <div className="flex-1 flex flex-col bg-white">
      <div className="flex-1 flex flex-col">
        <BreadcrumbsItem to={url}>{pageTitle}</BreadcrumbsItem>
        <Router history={history}>
          <LocalizedSwitch>
            <Route exact={true} path={AppRoute.KaikkiJarjestajat}>
              <article className="mx-auto w-4/5 mt-12 max-w-8xl">
                <Typography component="h1" variant="h1">
                  {pageTitle}
                </Typography>
                <p className="max-w-213 mb-6">
                  {formatMessage(kaikkiJarjestajat.sivunKuvaus)}
                </p>
                <p className="max-w-213 mb-6">
                  {formatMessage(kaikkiJarjestajat.maara, {
                    kpl: jarjestajat.paged?.data?.totalElements || 0
                  })}
                </p>
                <section>
                  <Fragment>
                    {jarjestajat.paged?.isErroneous && (
                      <Typography color="error" className="mb-4">
                        {formatMessage(error.virheLatauksessa)}
                      </Typography>
                    )}
                    {jarjestajat.paged?.isLoading &&
                      !lastKnownData.current && <Loading />}
                    {displayData?.content && (
                      <Jarjestajaluettelo
                        jarjestajat={displayData}
                        pageNo={pageNo}
                        pageSize={pageSize}
                        setPageNo={setPageNo}
                        setPageSize={setPageSize}
                        selectedKoulutustyyppi={selectedKoulutustyyppi}
                        setSelectedKoulutustyyppi={setSelectedKoulutustyyppi}
                        sortedBy={sortedBy}
                        onSortChange={setSortedBy}
                        searchBy={searchBy}
                        onSearchChange={setSearchBy}
                        openRows={openRows}
                        onOpenRowsChange={setOpenRows}
                        isLoading={jarjestajat.paged?.isLoading ?? false}
                      />
                    )}
                  </Fragment>
                </section>
              </article>
            </Route>
            <Route path="*">
              <Redirect to="/" />
            </Route>
          </LocalizedSwitch>
        </Router>
      </div>
    </div>
  );
};

export default KaikkiJarjestajat;
